import { z } from "zod";

/**
 * The home board's stored format (ADMIN-256): the new tab's widget grid as one small JSON
 * document. Both the API (validation before a save) and the extension (the grid, the widget
 * registry) use this module, through `@kourai/api/home-board`, so the format has one definition.
 *
 * It's our own format on purpose: the grid library (react-grid-layout) is mapped to it at the
 * edge, so the library can be swapped without migrating anyone's board.
 *
 * Kept free of anything API-specific (no Hono, no D1): it moves to the open source widgets SDK
 * with ADMIN-271.
 */

/** Columns of the grid. Sizes and positions are in cells. */
export const BOARD_COLS = 8;
/** The grid never grows past this many rows, which keeps a stored board bounded. */
export const BOARD_MAX_ROWS = 200;
/** A board holds at most this many widgets, the command box included. */
export const BOARD_MAX_ITEMS = 40;
/** One widget's `config`, as JSON, stays under this many bytes. Content goes in widget data. */
export const WIDGET_CONFIG_MAX_BYTES = 8 * 1024;

/** The command box: always on the board, always the first row, never removed. */
export const COMMAND_BOX_ID = "bar";
export const COMMAND_BOX_TYPE = "kourai.command";

const configBytes = (config: unknown) => new TextEncoder().encode(JSON.stringify(config)).length;

export const widgetInstanceSchema = z
  .object({
    /** Stable across moves and resizes; widget data is keyed by it. */
    id: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[\w-]+$/),
    /** The widget's registered type: a dotted, lowercase name such as "kourai.notes". */
    type: z
      .string()
      .max(80)
      .regex(/^[a-z0-9-]+(\.[a-z0-9-]+)+$/),
    /** The version of the widget's config format, so a widget can migrate its own config. */
    typeVersion: z.number().int().min(1).max(1000),
    x: z
      .number()
      .int()
      .min(0)
      .max(BOARD_COLS - 1),
    y: z
      .number()
      .int()
      .min(0)
      .max(BOARD_MAX_ROWS - 1),
    w: z.number().int().min(1).max(BOARD_COLS),
    h: z.number().int().min(1).max(12),
    config: z
      .record(z.string(), z.unknown())
      .refine((c) => configBytes(c) <= WIDGET_CONFIG_MAX_BYTES, {
        message: `config is over ${WIDGET_CONFIG_MAX_BYTES} bytes`,
      }),
  })
  .refine((i) => i.x + i.w <= BOARD_COLS, { message: "widget is wider than the grid" })
  .refine((i) => i.y + i.h <= BOARD_MAX_ROWS, { message: "widget is below the last row" });

export const boardSchema = z
  .object({
    schema: z.literal(1),
    cols: z.literal(BOARD_COLS),
    items: z.array(widgetInstanceSchema).max(BOARD_MAX_ITEMS),
  })
  .refine((b) => new Set(b.items.map((i) => i.id)).size === b.items.length, {
    message: "widget ids must be unique",
  });

export type WidgetInstance = z.infer<typeof widgetInstanceSchema>;
export type Board = z.infer<typeof boardSchema>;

/** Where the board a GET returned came from. */
export type BoardSource = "member" | "starter" | "default";

/**
 * What `GET /api/workspaces/:id/home` returns (and a 409 carries). `version` is 0 when you have
 * no board of your own yet (`source` is then "starter" or "default"): the first save sends
 * `If-Match: 0` and creates yours.
 */
export type HomeBoardDto = {
  board: Board;
  version: number;
  source: BoardSource;
  /** The workspace has a starter board (ADMIN-264), at this version. Absent when it has none. */
  starterVersion?: number;
  /**
   * You have your own board and the workspace published a starter you haven't been offered yet:
   * "Use it or keep your own". Absent otherwise.
   */
  starterOffer?: { board: Board; version: number };
};

const COMMAND_BOX: WidgetInstance = {
  id: COMMAND_BOX_ID,
  type: COMMAND_BOX_TYPE,
  typeVersion: 1,
  x: 0,
  y: 0,
  w: BOARD_COLS,
  h: 1,
  config: {},
};

/**
 * The built-in board everyone starts from: the Focus starter (Tasks, Notes, the time) with Links in
 * the space next to the clock. Its Tasks start with a few example tasks to try.
 */
export const DEFAULT_BOARD: Board = {
  schema: 1,
  cols: BOARD_COLS,
  items: [
    COMMAND_BOX,
    {
      id: "tasks",
      type: "kourai.tasks",
      typeVersion: 1,
      x: 0,
      y: 1,
      w: 4,
      h: 4,
      config: { examples: true },
    },
    { id: "notes", type: "kourai.notes", typeVersion: 1, x: 4, y: 1, w: 4, h: 2, config: {} },
    { id: "clock", type: "kourai.clock", typeVersion: 1, x: 4, y: 3, w: 2, h: 2, config: {} },
    { id: "links", type: "kourai.links", typeVersion: 1, x: 6, y: 3, w: 2, h: 2, config: {} },
  ],
};

/** A fresh copy of the default board (callers may mutate what they get). */
export const defaultBoard = (): Board => structuredClone(DEFAULT_BOARD);

/** A built-in starting point people (and workspace admins) can pick (ADMIN-263, ADMIN-264). */
export type StarterBoard = {
  id: "focus" | "research" | "team";
  name: string;
  description: string;
  board: Board;
};

const at = (
  id: string,
  type: string,
  x: number,
  y: number,
  w: number,
  h: number,
): WidgetInstance => ({ id, type, typeVersion: 1, x, y, w, h, config: {} });

const starter = (...items: WidgetInstance[]): Board => ({
  schema: 1,
  cols: BOARD_COLS,
  items: [structuredClone(COMMAND_BOX), ...items],
});

/** The three built-in starters, in the order they're offered. */
export const STARTER_BOARDS: readonly StarterBoard[] = [
  {
    id: "focus",
    name: "Focus",
    description: "Tasks, notes and the time",
    board: starter(
      at("tasks", "kourai.tasks", 0, 1, 4, 4),
      at("notes", "kourai.notes", 4, 1, 4, 2),
      at("clock", "kourai.clock", 4, 3, 2, 2),
    ),
  },
  {
    id: "research",
    name: "Research",
    description: "Recent, pinned chats and your links",
    board: starter(
      at("recent", "kourai.recent", 0, 1, 4, 4),
      at("pinned", "kourai.pinned", 4, 1, 4, 2),
      at("links", "kourai.links", 4, 3, 4, 2),
    ),
  },
  {
    id: "team",
    name: "Team",
    description: "Pinned chats over Recent",
    board: starter(
      at("pinned", "kourai.pinned", 0, 1, 8, 2),
      at("recent", "kourai.recent", 0, 3, 8, 4),
    ),
  },
];
